import { useReactivateShippingLocationMutation } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import Modal from '@/gf/components/ModalNext'
import useMsgs from '@/gf/hooks/useMsgs'
import { ApolloError } from '@apollo/client'
import { useState } from 'react'

interface Props {
  locationId: string
  locationName: string
  open: boolean
  onClose: () => void
  onDataChanged: () => void
}

const ReactivateLocationModal = ({
  locationId,
  locationName,
  open,
  onClose,
  onDataChanged,
}: Props) => {
  const client = useGqlClient()
  const [_, msgr] = useMsgs()
  const [reactivateLocation] = useReactivateShippingLocationMutation({ client })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleReactivate = async () => {
    setIsSubmitting(true)

    try {
      await reactivateLocation({
        variables: { id: locationId },
      })

      msgr.add('Location reactivated successfully.', 'positive')
      onDataChanged()
      onClose()
    } catch (err) {
      const error = err as ApolloError
      const message = error.graphQLErrors.reduce((acc, e) => `${acc}${e.message}`, '')
      msgr.add(message || 'Failed to reactivate location', 'negative')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <Modal onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Reactivate Location</h3>
          <CloseModalButton onClick={onClose} />
        </div>

        <div className="mb-6 space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to reactivate <strong>{locationName}</strong>?
          </p>

          <p className="text-sm text-gray-500">
            This will make the location visible and available for use again.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <Action.S onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Action.S>
          <Action.P
            color="blue"
            onClick={handleReactivate}
            performing={isSubmitting}
            disabled={isSubmitting}
          >
            Reactivate
          </Action.P>
        </div>
      </div>
    </Modal>
  )
}

export default ReactivateLocationModal
